import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, Form, message, Typography, List, Row, Col, Layout, Pagination, Space, Breadcrumb } from 'antd';
import { createOrUpdateBusinessInfoEntry, createOrUpdateQAEntry, getEntries, getKnowledgeDatabase, deleteEntry } from '../KnowledgeDatabaseServices';
import { useGlobal } from '../../Shared/GlobalContext/GlobalContext';
import EntryItem from './EntryItem/EntryItem';
import { Content } from 'antd/es/layout/layout';
import DatabaseKPIs from './DatabaseKPIs/DatabaseKPIs';
import NewEntryForm from './NewEntryForm/NewEntryForm';
import { DatabaseOutlined } from '@ant-design/icons';


const { Text, Title } = Typography;

const KnowledgeDatabaseDetail = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const { loading, setLoading } = useGlobal();
    const [entries, setEntries] = useState([]);
    const [databaseInfo, setDatabaseInfo] = useState(null);
    const [form] = Form.useForm();
    const [editingEntry, setEditingEntry] = useState(null);
    const formRef = useRef(null);

    const [paginationMetadata, setPaginationMetadata] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });

    const fetchEntries = useCallback(async (databaseId, page = 1) => {
        try {
            setLoading(true);
            const { data, success, metadata } = await getEntries(databaseId, page);
            if (success) {
                setEntries(data);
                setPaginationMetadata({
                    current: metadata.page,
                    pageSize: metadata.per_page,
                    total: metadata.total
                });
            } else {
                message.error(data.message || t('failed_to_fetch_entries'));
            }
        } catch (error) {
            message.error(error.response?.data?.message || t('error_fetching_entries'));
        } finally {
            setLoading(false);
        }
    }, []);

    const handlePageChange = (page) => {
        fetchEntries(id, page);
    };

    const fetchDatabaseInfo = useCallback(async (localId) => {
        if (!localId) {
            message.error(t('no_database_id_provided'));
            return;
        }
        try {
            setLoading(true);
            const { data } = await getKnowledgeDatabase(localId);
            setDatabaseInfo(data);
        } catch (error) {
            message.error(error.response?.data?.message || t('error_fetching_database_information'));
        } finally {
            setLoading(false);
        }
    }, []);

    const handleCreateOrUpdateEntry = useCallback(async (values) => {
        if (!id) {
            message.error(t('no_database_id_provided'));
            return;
        }
        try {
            setLoading(true);
            const api = values.entryType === 'qa' ? createOrUpdateQAEntry : createOrUpdateBusinessInfoEntry;
            const entryData = values.entryType === 'qa'
                ? {
                    knowledge_base_id: id,
                    question: values.question,
                    answer: values.answer
                }
                : {
                    knowledge_base_id: id,
                    content: values.content
                };

            if (editingEntry) {
                entryData.id = editingEntry.id;
            }

            await api(entryData);
            form.resetFields();
            setEditingEntry(null);
            await fetchEntries(id, paginationMetadata.current);
            await fetchDatabaseInfo(id);
            message.success(editingEntry ? t('entry_updated_successfully') : t('entry_created_successfully'));
        } catch (error) {
            if (error.response?.status === 401) {
                message.error(t('unauthorized_access'));
            } else {
                message.error(error.response?.data?.message || t('error_creating_updating_entry'));
            }
        } finally {
            setLoading(false);
        }
    }, [id, form,
        fetchEntries, fetchDatabaseInfo, editingEntry, paginationMetadata,
        setLoading]);

    const handleEditEntry = (entry) => {
        setEditingEntry(entry);
        form.setFieldsValue({
            entryType: entry.entry_type === 'question_answer' ? 'qa' : 'business',
            question: entry.data.question,
            answer: entry.data.answer,
            content: entry.data.content
        });
        if (formRef.current) {
            formRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const handleDeleteEntry = async (entryId) => {
        try {
            const { success } = await deleteEntry(entryId);
            if (success) {
                message.success(t('entry_deleted_successfully'));
                const newPage = entries.length === 1 && paginationMetadata.current > 1
                    ? paginationMetadata.current - 1
                    : paginationMetadata.current;
                await fetchEntries(id, newPage);
                await fetchDatabaseInfo(id);
            } else {
                message.error(t('failed_to_delete_entry'));
            }
        } catch (error) {
            message.error(error.response?.data?.message || t('error_deleting_entry'));
        }
    };

    useEffect(() => {
        if (id) {
            fetchDatabaseInfo(id);
            fetchEntries(id);
        }
    }, [id, fetchDatabaseInfo, fetchEntries]);

    if (!id) {
        return (
            <Layout>
                <Content>
                    <Row justify="center">
                        <Col xs={24} md={12}>
                            <Card>
                                <Text type="danger">{t('no_database_id_provided_check_url')}</Text>
                            </Card>
                        </Col>
                    </Row>
                </Content>
            </Layout>
        );
    }
    return (
        <Layout>
            <Content>
                <Row style={{ marginBottom: '8px' }}>
                    <Col span={24}>
                        <Breadcrumb
                            items={[
                                {
                                    title: <Link to="/knowledge-base">
                                        <DatabaseOutlined /> {t('knowledge_bases')}
                                    </Link>,
                                },
                                {
                                    title: databaseInfo?.name || t('loading'),
                                },
                            ]}
                        />
                    </Col>
                </Row>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <DatabaseKPIs databaseInfo={databaseInfo} refresh={() => fetchDatabaseInfo(id)} />
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={8}>
                            <NewEntryForm
                                onSubmit={handleCreateOrUpdateEntry}
                                editingEntry={editingEntry}
                                onCancel={() => setEditingEntry(null)}
                                loading={loading}
                                formRef={formRef}
                            />
                        </Col>
                        <Col xs={24} md={16}>
                            {entries.length > 0 && (
                                <Card
                                    bordered={false}
                                    style={{
                                        borderRadius: '8px'
                                    }}>
                                    <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                        <Title level={3} style={{ margin: 0 }}>
                                            {t('existing_entries')}
                                        </Title>
                                        <List
                                            dataSource={entries}
                                            renderItem={item => (
                                                <EntryItem
                                                    key={item?.id}
                                                    item={item}
                                                    onEdit={handleEditEntry}
                                                    onDelete={() => handleDeleteEntry(item.id)}
                                                />
                                            )}
                                        />
                                        {paginationMetadata.total > paginationMetadata.pageSize && (
                                            <Row justify="end">
                                                <Pagination
                                                    current={paginationMetadata.current}
                                                    pageSize={paginationMetadata.pageSize}
                                                    total={paginationMetadata.total}
                                                    onChange={handlePageChange}
                                                    showSizeChanger={false}
                                                />
                                            </Row>
                                        )}
                                    </Space>
                                </Card>
                            )}
                        </Col>
                    </Row>
                </Space>
            </Content>
        </Layout>
    );
};

export default KnowledgeDatabaseDetail;
